/**
 * COMPONENT: defer-image (Tier 3 — Full Web Component)
 * Lazy-loads images on intersection using IntersectionObserver.
 *
 * Modes:
 *   Default   — renders an <img> tag on intersection
 *   Background — sets background-image on the element itself
 *
 * Attributes:
 *   src             — image source URL (required)
 *   width           — intrinsic width for aspect ratio
 *   height          — intrinsic height for aspect ratio
 *   alt             — alt text for accessibility
 *   type            — "background" for bg mode, omit for inline img
 *   position        — background-position or object-position override
 *   position-responsive — object-position for smaller screens
 *   fit             — object-fit value (e.g. "cover")
 *   max-width       — max-width constraint on the element
 *   srcset          — responsive sources, format: "url|breakpoint,url|breakpoint"
 *
 * Usage:
 *   <defer-image src="photo.jpg" width="800" height="600" alt="Description"></defer-image>
 *   <defer-image src="hero.jpg" type="background"></defer-image>
 */

class DeferImage extends HTMLElement {

  constructor() {
    super();
    this.loaded = false;
  }

  /**
   * Sanitize a value for safe injection into CSS.
   */
  _sanitizeCSS(value) {
    if (!value || typeof value !== 'string') return '';
    return value
      .replace(/[{}<>;'"\\]/g, '')
      .replace(/url\s*\(/gi, '')
      .replace(/expression\s*\(/gi, '')
      .replace(/javascript\s*:/gi, '')
      .trim();
  }

  connectedCallback() {
    const src = this.getAttribute('src');
    if (!src) return;

    // Set aspect ratio placeholder for inline images
    const type = this.getAttribute('type') || 'normal';
    const width = this.getAttribute('width') || '100%';
    const height = this.getAttribute('height') || 0;

    if (type !== 'background') {
      this.style.aspectRatio = `${width} / ${height}`;
    }

    this.setAttribute('loading', '');
    this._observe();
  }

  _observe() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.loaded) {
          this._load();
          observer.unobserve(this);
        }
      },
      { rootMargin: '0px', threshold: 0.2 }
    );
    observer.observe(this);
  }

  _load() {
    const src = this.getAttribute('src');
    const type = this.getAttribute('type') || 'normal';
    const width = this.getAttribute('width') || '100%';
    const height = this.getAttribute('height') || 0;
    const alt = this.getAttribute('alt') || '';
    const maxWidth = this.getAttribute('max-width') || null;
    const srcset = this.getAttribute('srcset')
      ? this.getAttribute('srcset').split(',').map(s => s.split('|'))
      : null;

    // Sanitize values that get injected into <style> tags
    const position = this._sanitizeCSS(this.getAttribute('position')) || null;
    const positionResp = this._sanitizeCSS(this.getAttribute('position-responsive')) || null;
    const fit = this._sanitizeCSS(this.getAttribute('fit')) || 'normal';

    const imgId = 'img-' + Math.random().toString(16).slice(2);

    if (type === 'background' && !srcset) {
      this._loadBgSimple(src, position, width, height);
    } else if (type === 'background' && srcset) {
      this._loadBgSrcset(src, alt, imgId, srcset, position, positionResp);
    } else {
      this._loadInline(src, alt, imgId, width, height, fit, position, maxWidth, srcset);
    }

    this.loaded = true;
  }

  _loadBgSimple(src, position, width, height) {
    this.style.backgroundImage = `url(${src})`;
    if (position) this.style.backgroundPosition = position;
    this.style.aspectRatio = `${width} / ${height}`;
    this._done();
  }

  _loadBgSrcset(src, alt, imgId, srcset, position, positionResp) {
    this.removeAttribute('style');
    this.innerHTML = `<picture>
      ${srcset.map(s => `<source srcset="${s[0]}" media="(max-width: ${s[1]})" />`).join('')}
      <img id="${imgId}" src="${src}" alt="${alt}"/>
    </picture>`;

    if (position) {
      let style = `<style>#${imgId}{object-position:${position}}`;
      if (positionResp) {
        srcset.forEach(s => {
          style += `@media screen and (max-width:${s[1]}){#${imgId}{object-position:${positionResp}}}`;
        });
      }
      style += `</style>`;
      this.insertAdjacentHTML('afterbegin', style);
    }

    this._waitForLoad(imgId);
  }

  _loadInline(src, alt, imgId, width, height, fit, position, maxWidth, srcset) {
    this.removeAttribute('style');

    if (fit !== 'normal') {
      let style = `<style>#${imgId}{object-fit:${fit}}`;
      if (position) style += `;object-position:${position}`;
      if (fit === 'cover') {
        this.style.width = '100%';
        this.style.height = '100%';
        style += `#${imgId}{width:100%;height:100%}`;
      }
      style += `</style>`;
      this.insertAdjacentHTML('beforebegin', style);
    }

    if (srcset) {
      const rawSrcset = this.getAttribute('srcset');
      const isStandard = !rawSrcset.includes('|');

      if (isStandard) {
        const rawSizes = this.getAttribute('sizes');
        this.innerHTML = `<img id="${imgId}" src="${src}" width="${width}" height="${height}" alt="${alt}" srcset="${rawSrcset}"${rawSizes ? ` sizes="${rawSizes}"` : ''}/>`;
      } else {
        this.innerHTML = `<picture>
        ${srcset.map(s => `<source srcset="${s[0]}" media="(max-width: ${s[1]})" />`).join('')}
            <img id="${imgId}" src="${src}" alt="${alt}"/>
        </picture>`;
      }
    } else {
      this.innerHTML = `<img id="${imgId}" src="${src}" width="${width}" height="${height}" alt="${alt}"/>`;
    }

    if (width && width !== '100%') {
      this.style.width = width;
    }

    setTimeout(() => {
      const img = this.querySelector('img');
      if (img) img.style.opacity = 1;
      if (maxWidth) this.style.maxWidth = maxWidth;
      this.style.backgroundColor = 'transparent';
      this._waitForLoad(imgId);
    }, 250);
  }

  _waitForLoad(imgId) {
    const img = this.querySelector(`#${imgId}`) || this.querySelector('img');
    if (!img) return this._done();

    if (img.complete) {
      this._done();
    } else {
      img.addEventListener('load', () => this._done());
    }
  }

  _done() {
    this.removeAttribute('loading');
    this.setAttribute('loaded', '');
  }
}

window.customElements.define('defer-image', DeferImage);
